import { Box, styled } from '@mui/material';
import { CSSProperties } from 'react';

import { ELayoutManagerMode } from '#shared';

import { Carousel, CarouselStatic } from '../../Carousel';
import { useAirFleetCardLayout } from '../../data-context';
import { useLayoutMode } from '../../LayoutManager';
import {
  AVAILABILITY_CARD_MAX_WIDTH,
  AVAILABILITY_CARD_MIN_HEIGHT,
  AVAILABILITY_CARD_MIN_WIDTH,
} from '../model';

import { AircraftCardAdapter } from './AircraftCardAdapter';

import type { AircraftData } from '#entities';

const ROW_HEADER_W = 40;
const ROW_GAP = 8;

const Root = styled(Box)(({ theme: { spacing, palette } }) => ({
  display: 'flex',
  width: '100%',
  minHeight: AVAILABILITY_CARD_MIN_HEIGHT,
  gap: spacing(1),

  [`&.availability-group-NA .row-header`]: {
    color: palette.error[700],
    backgroundColor: `${palette.error[700]}26`,
  },
  [`&.availability-group-CRITICAL .row-header`]: {
    color: palette.grey[700],
    backgroundColor: `${palette.grey[700]}26`,
  },
  [`&.availability-group-MAJOR .row-header`]: {
    color: palette.yellow[500],
    backgroundColor: `${palette.yellow[500]}26`,
  },
  [`&.availability-group-LOW .row-header`]: {
    color: palette.common.white,
    backgroundColor: `#FFFFFF26`,
  },

  [`.row-header`]: {
    width: ROW_HEADER_W,
    writingMode: 'sideways-lr',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    padding: spacing(2, 1),
    userSelect: 'none',
  },

  [`.row-body`]: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
  },
}));

type RowProps = {
  title: string;
  items: AircraftData[];
  onSelect: (a: AircraftData) => void;
  groupKey: string;
  rowsCount: number;
  perRowHeight: (rows: number) => number;
};

export const SeverityRow = ({
  groupKey = 'NA',
  title,
  items,
  onSelect,
  rowsCount,
  perRowHeight,
}: RowProps) => {
  const { mode } = useLayoutMode();
  const isPreviewMode = mode !== ELayoutManagerMode.RENDER;

  // cap rows so we never ask for more rows than items
  const effectiveRows = Math.min(rowsCount, Math.max(1, items.length));

  const { colWidth, refCallback } = useAirFleetCardLayout({
    itemsCount: items.length,
    maxRows: effectiveRows,
    minColWidth: AVAILABILITY_CARD_MIN_WIDTH,
    maxColWidth: AVAILABILITY_CARD_MAX_WIDTH,
    minRowHeight: AVAILABILITY_CARD_MIN_HEIGHT,
    gap: ROW_GAP,
  });

  if (!effectiveRows || items.length === 0) {
    return null;
  }

  const trackH = perRowHeight(rowsCount);

  const itemProps = {
    width: colWidth,
    fullSize: true,
    onClickId: onSelect,
  };

  const CarouselComponent = isPreviewMode ? CarouselStatic : Carousel;

  return (
    <Root
      className={`availability-group-${groupKey}`}
      style={
        {
          height: 'auto',
          ['--row-h']: `${AVAILABILITY_CARD_MIN_HEIGHT}px`,
        } as CSSProperties
      }
    >
      <Box className="row-header">{title}</Box>
      <Box className="row-body" ref={refCallback}>
        <CarouselComponent
          renderItem={data => (
            <AircraftCardAdapter data={data} {...itemProps} />
          )}
          items={items}
          itemKey="Id"
          normalSize={effectiveRows}
          gap={ROW_GAP}
          itemWidth={colWidth}
          scrollStepSize={isPreviewMode ? undefined : 1}
          loopInterval={isPreviewMode ? undefined : 8000}
          normalStretch
          normalMinSize={trackH}
        />
      </Box>
    </Root>
  );
};
